import React, { useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Container,
    Card,
    AppBar,
    Toolbar,
    IconButton,
    Button,
    Collapse,
    Paper,
    Divider
} from "@mui/material";
import {
    Menu as MenuIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import Logger from "./utils/Logger";

const ErrorPage = ({ error }) => {
    const { toggleMobileDrawer } = useOutletContext() || {};
    const [showLogs, setShowLogs] = useState(false);
    const navigate = useNavigate();
    const logs = Logger.getLogs();

    // Determine error message string
    const errorMessage = typeof error === 'string'
        ? error
        : error?.message || JSON.stringify(error) || "An unexpected error occurred.";

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%', bgcolor: 'background.default' }}>
            {/* Page Header */}
            <AppBar position="static" elevation={0} sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Toolbar sx={{ px: { xs: 2, sm: 3 } }}>
                    {toggleMobileDrawer && (
                        <IconButton
                            onClick={toggleMobileDrawer}
                            sx={{ display: { xs: 'flex', md: 'none' }, color: 'text.primary', mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}
                    <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: -0.5 }}>
                        Error
                    </Typography>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md" sx={{ flexGrow: 1, py: 4 }}>
                <Card sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 3 }}>
                    <Typography variant="h5" color="error" fontWeight={700} gutterBottom>
                        Something went wrong
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {errorMessage}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => setShowLogs(!showLogs)}
                            endIcon={showLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        >
                            {showLogs ? "Hide Logs" : "Show Logs"}
                        </Button>
                    </Box>
                </Card>

                <Collapse in={showLogs}>
                    <Paper sx={{ p: 2, bgcolor: '#0d1117', color: '#e6edf3', borderRadius: 2, overflowX: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#8b949e' }}>
                            Application Logs (Last {logs.length})
                        </Typography>
                        <Divider sx={{ bgcolor: '#30363d', mb: 2 }} />
                        <Box sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {logs.length === 0 ? (
                                <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#8b949e' }}>
                                    No logs recorded.
                                </Typography>
                            ) : (
                                logs.map((log, i) => (
                                    <Box key={i} sx={{ mb: 1, pb: 1, borderBottom: '1px solid #30363d', opacity: log.level === 'ERROR' ? 1 : 0.8 }}>
                                        <Typography component="span" sx={{ color: '#58a6ff', mr: 1 }}>
                                            [{log.timestamp.split('T')[1].split('.')[0]}]
                                        </Typography>
                                        <Typography component="span" sx={{
                                            color: log.level === 'ERROR' ? '#f85149' : (log.level === 'WARN' ? '#d29922' : '#3fb950'),
                                            fontWeight: 'bold',
                                            mr: 1
                                        }}>
                                            {log.level}
                                        </Typography>
                                        <Typography component="span" sx={{ color: '#8b949e', mr: 1 }}>
                                            [{log.module}]
                                        </Typography>
                                        <Typography component="span">
                                            {log.message}
                                        </Typography>
                                        {log.data && (
                                            <Box sx={{ pl: 4, mt: 0.5, color: '#8b949e', whiteSpace: 'pre-wrap' }}>
                                                {JSON.stringify(log.data, null, 2)}
                                            </Box>
                                        )}
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Paper>
                </Collapse>
            </Container>
        </Box>
    );
};

export default ErrorPage;